import UsageProgressBar from "./UsageProgressBar";
import { KTIcon } from "helpers";
import { getFeatureDetails } from "helpers/utils";
import { useAuth } from "hooks‬/Auth";
import React, { useEffect, useState } from "react";
import { FaLayerGroup } from "react-icons/fa";
import { MdScheduleSend } from "react-icons/md";
import { RiRobot2Fill } from "react-icons/ri";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import { privateRoutes } from "constant/route/privateRoutes";

const SapiensUsage = () => {
  const navigate = useNavigate();
  const { companyProductSubscriptionDetails } = useAuth();
  const [sapiensSubscriptionDetails, setSapiensSubscriptionDetails] =
    useState<any>();

  useEffect(() => {
    console.log(companyProductSubscriptionDetails);
    const com = companyProductSubscriptionDetails.find(
      (product: any) =>
        product.subscriptionPricingPlan.subscriptionPlanProduct.productType
          .name === "SAPIENS",
    );
    console.log(com);
    setSapiensSubscriptionDetails(com);
  }, [companyProductSubscriptionDetails]);

  /* ============================================================
     Derived display data — pure rendering, no logic changes
     ============================================================ */
  const planName =
    sapiensSubscriptionDetails?.subscriptionPricingPlan
      ?.subscriptionPlanProduct?.subscriptionPlan?.subscriptionPlanDisplayName;
  const endDate = sapiensSubscriptionDetails?.endDate;
  const features =
    sapiensSubscriptionDetails?.subscriptionPlanProductFeature ?? [];
  const usageItems =
    sapiensSubscriptionDetails?.companySubscriptionUsage ?? [];
  const daysRemaining = endDate
    ? moment(endDate).startOf("day").diff(moment().startOf("day"), "days")
    : null;
  const daysUrgent = daysRemaining !== null && daysRemaining <= 7;

  return (
    <>
      {/* PLAN HERO CARD */}
      <div className="ni-usage-plan-hero">
        <div className="ni-usage-plan-top">
          <div className="ni-usage-plan-meta">
            <div className="ni-usage-plan-eyebrow">Current plan · Sapiens</div>
            <h2 className="ni-usage-plan-name">{planName || "—"}</h2>
            <div className="ni-usage-plan-pills">
              {endDate && (
                <span className="ni-usage-plan-pill ni-usage-plan-pill--active">
                  Active until {moment(endDate).format("Do MMM YYYY")}
                </span>
              )}
              {daysRemaining !== null && daysRemaining >= 0 && (
                <span
                  className={`ni-usage-plan-pill ni-usage-plan-pill--days ${
                    daysUrgent ? "urgent" : ""
                  }`}
                >
                  {daysRemaining === 0
                    ? "Expires today"
                    : `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} left`}
                </span>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={() => navigate(privateRoutes.Pricing)}
            className="ni-usage-plan-cta"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="12" y1="19" x2="12" y2="5" />
              <polyline points="5 12 12 5 19 12" />
            </svg>
            Upgrade Plan
          </button>
        </div>

        <ul className="ni-usage-feature-grid" aria-label="Plan features">
          {features.map((item: any) => (
            <li
              key={item.subscriptionPlanProductFeatureGuid}
              className="ni-usage-feature-row"
            >
              <span className="ni-usage-feature-row-icon" aria-hidden="true">
                <svg
                  width="9"
                  height="9"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <span className="ni-usage-feature-row-text">
                {item.feature?.featuresDisplayName && (
                  <span className="ni-usage-feature-row-label">
                    {item.feature.featuresDisplayName}:{" "}
                  </span>
                )}
                {item.displayName}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* USAGE BREAKDOWN — full width for Sapiens (no graphs) */}
      <div className="ni-usage-card">
        <div className="ni-usage-card-header">
          <h3 className="ni-usage-card-title">
            <div className="ni-usage-card-title-icon" aria-hidden="true">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
              </svg>
            </div>
            Usage breakdown
          </h3>
          <div className="ni-usage-card-meta">
            {usageItems.length}{" "}
            {usageItems.length === 1 ? "metric" : "metrics"}
          </div>
        </div>

        {usageItems.length === 0 ? (
          <div className="ni-usage-empty" style={{ border: "none", margin: 0 }}>
            <div className="ni-usage-empty-icon">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h4 className="ni-usage-empty-title">No usage data yet</h4>
            <p className="ni-usage-empty-sub">
              Usage metrics will appear here once you start using Sapiens.
            </p>
          </div>
        ) : (
          usageItems.map((item: any) => {
            const total = item.currentCount + item.totalAvailableCount;
            const { percentage, label, iconName, displayText, isToggleFeature } =
              getFeatureDetails(
                item.currentCount,
                total,
                item.features.featuresDisplayName,
              );

            let iconElement: React.ReactNode;
            let toneClass = "";
            if (iconName === "AI") {
              iconElement = <RiRobot2Fill />;
              toneClass = "ai";
            } else if (iconName === "round") {
              iconElement = <FaLayerGroup />;
              toneClass = "layer";
            } else if (iconName === "schedule") {
              iconElement = <MdScheduleSend />;
              toneClass = "schedule";
            } else {
              iconElement = <KTIcon iconName={iconName} className="" />;
            }

            return (
              <div
                key={item.features.featuresGuid}
                className="ni-usage-item"
              >
                <div className="ni-usage-item-header">
                  <div className="ni-usage-item-label-wrap">
                    <div
                      className={`ni-usage-item-icon ${toneClass}`}
                      aria-hidden="true"
                    >
                      {iconElement}
                    </div>
                    <div className="ni-usage-item-label">{label}</div>
                  </div>
                  <div className="ni-usage-item-count">
                    {total === 0 || total >= 999999 || isToggleFeature ? (
                      displayText
                    ) : (
                      <>
                        <strong>{item.currentCount}</strong> of {total} used
                      </>
                    )}
                  </div>
                </div>
                {!isToggleFeature && <UsageProgressBar completion={percentage} />}
              </div>
            );
          })
        )}
      </div>
    </>
  );
};

export default SapiensUsage;
